import { useMemo, type ReactNode } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowRight, Clock3, RotateCcw, Sparkles, Trophy } from 'lucide-react';
import type { ReviseHistoryItem } from '@formency/shared';
import { Button, PageContainer, Surface, Badge, Tag } from '@/interfaces/learner/ui';
import { useReviseHistory } from '@/shared/api/hooks';
import { ReviseSession } from '@/shared/illustrations/ReviseSession';
import { formatTimeOfDay } from '@/shared/utils/formatDuration';
import {
  formatRevisionDurationMinutes,
  formatRevisionQuestionSummary,
  formatRevisionScore,
  getRevisionBadgeVariant,
  getRevisionScoreColor,
  getRevisionStatusLabel,
  groupByDate,
  summariseRevisionHistory,
} from './revisionUtils';

const EASE = [0.16, 1, 0.3, 1] as const;
const PAGE_SIZE = 20;

export default function RevisionPage() {
  const navigate = useNavigate();
  const historyQuery = useReviseHistory(PAGE_SIZE);

  const historyItems = useMemo(
    () =>
      (historyQuery.data?.pages.flatMap((page) => page.data) ?? []).sort(
        (a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
      ),
    [historyQuery.data]
  );

  const summary = useMemo(() => summariseRevisionHistory(historyItems), [historyItems]);
  const groups = useMemo(() => groupByDate(historyItems), [historyItems]);

  return (
    <PageContainer width="wide" className="relative pb-24">
      <section style={{ paddingTop: 'var(--page-top-spacing)' }}>
        <div className="grid gap-6 learner-double:grid-cols-[minmax(0,1fr)_380px]">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: EASE }}
          >
            <p className="text-eyebrow" style={{ color: 'var(--color-accent)', marginBottom: 10 }}>
              Revision
            </p>
            <h1 className="text-hero-title" style={{ maxWidth: 'var(--measure-hero)' }}>
              Keep a clear record of what you&apos;ve revised and what needs another pass.
            </h1>
            <p className="text-body mt-4" style={{ maxWidth: 'var(--measure-copy)' }}>
              Every completed revision session becomes part of your recall trail. Review strong
              sessions, revisit weaker ones, and use the history here to decide what deserves your
              next round of attention.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.08, ease: EASE }}
          >
            <Surface
              tone="solid"
              padding={24}
              style={{ background: 'var(--color-accent-wash-strong)' }}
            >
              <div className="flex items-start gap-5">
                <div
                  className="flex h-24 w-24 shrink-0 items-center justify-center rounded-[24px]"
                  style={{
                    background:
                      'linear-gradient(180deg, rgba(255,255,255,0.82), rgba(255,255,255,0.55))',
                    border: '1px solid var(--color-accent-outline)',
                    boxShadow: 'var(--control-shadow-subtle)',
                  }}
                >
                  <ReviseSession
                    variant="on-gradient"
                    style={{ width: 68, height: 80, opacity: 0.96 }}
                  />
                </div>

                <div className="min-w-0">
                  <p className="text-eyebrow" style={{ color: 'var(--color-accent-text)' }}>
                    Overview
                  </p>
                  <p className="text-panel-title mt-3" style={{ maxWidth: '18ch' }}>
                    A calmer way to spot strong recall and unfinished work.
                  </p>
                  <p className="text-meta mt-3" style={{ maxWidth: 'var(--measure-panel-copy)' }}>
                    Strong sessions deserve repeating at the right interval. Abandoned or
                    lower-score sessions are the ones to revisit first.
                  </p>
                </div>
              </div>
            </Surface>
          </motion.div>
        </div>

        <motion.div
          className="mt-8 grid gap-4 learner-single:grid-cols-2 learner-double:grid-cols-4"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.12, ease: EASE }}
        >
          <SummaryCard
            icon={<RotateCcw size={18} strokeWidth={2.1} />}
            label="Total sessions"
            value={String(summary.totalSessions)}
            description="Completed and abandoned revision attempts."
          />
          <SummaryCard
            icon={<Sparkles size={18} strokeWidth={2.1} />}
            label="Average accuracy"
            value={
              summary.averageAccuracy != null ? `${summary.averageAccuracy}%` : 'No scores yet'
            }
            valueColor={
              summary.averageAccuracy != null ? 'var(--color-accent-text)' : 'var(--color-text)'
            }
            description="Across sessions that reached a marked result."
          />
          <SummaryCard
            icon={<Trophy size={18} strokeWidth={2.1} />}
            label="Best session"
            value={summary.bestScore != null ? `${summary.bestScore}%` : '—'}
            valueColor={summary.bestScore != null ? 'var(--color-success-text)' : undefined}
            description="Your strongest recorded revision score."
          />
          <SummaryCard
            icon={<Clock3 size={18} strokeWidth={2.1} />}
            label="Time revised"
            value={summary.totalDurationLabel}
            description="Total time spent in completed revision sessions."
          />
        </motion.div>

        <motion.div
          className="mt-10"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.18, ease: EASE }}
        >
          {historyQuery.isLoading ? (
            <HistorySkeleton />
          ) : historyQuery.isError ? (
            <StateCard
              eyebrow="Couldn’t load history"
              title="Your revision sessions aren’t available right now."
              body="Try again in a moment. If the problem persists, the rest of the learner area is still unaffected."
              action={
                <Button variant="accent" onPress={() => void historyQuery.refetch()}>
                  Try again
                </Button>
              }
            />
          ) : historyItems.length === 0 ? (
            <StateCard
              eyebrow="No sessions yet"
              title="Once you finish a revision session, it will show up here."
              body="Start a revision session from your learner home, complete it, and you’ll be able to review the results step by step."
              illustration={
                <div
                  className="flex h-28 w-28 items-center justify-center rounded-[28px]"
                  style={{
                    background: 'var(--color-accent-wash-strong)',
                    border: '1px solid var(--color-accent-outline)',
                  }}
                >
                  <ReviseSession variant="on-gradient" style={{ width: 78, height: 92 }} />
                </div>
              }
              action={
                <Button variant="accent" onPress={() => navigate('/')}>
                  Start revision
                </Button>
              }
            />
          ) : (
            <>
              <div className="flex items-end justify-between gap-4">
                <div>
                  <p className="text-eyebrow" style={{ color: 'var(--color-text-muted)' }}>
                    Session history
                  </p>
                  <h2 className="text-section-title mt-2">Review past sessions day by day.</h2>
                </div>
                <Badge variant="surface">{historyItems.length} sessions</Badge>
              </div>

              <div className="mt-8 space-y-10">
                {groups.map((group) => (
                  <section key={group.key}>
                    <div className="flex items-center gap-3">
                      <p className="text-eyebrow" style={{ color: 'var(--color-accent-text)' }}>
                        {group.label}
                      </p>
                      <div
                        className="h-px flex-1"
                        style={{ background: 'var(--color-accent-line)' }}
                      />
                    </div>

                    <div className="mt-4 grid gap-4 learner-double:grid-cols-2">
                      {group.items.map((item, index) => (
                        <SessionCard key={item.id} item={item} index={index} />
                      ))}
                    </div>
                  </section>
                ))}
              </div>

              {historyQuery.hasNextPage && (
                <div className="mt-8 flex justify-center">
                  <Button
                    variant="secondary"
                    onPress={() => void historyQuery.fetchNextPage()}
                    isDisabled={historyQuery.isFetchingNextPage}
                  >
                    {historyQuery.isFetchingNextPage ? 'Loading more…' : 'Load more sessions'}
                  </Button>
                </div>
              )}
            </>
          )}
        </motion.div>
      </section>
    </PageContainer>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  description,
  valueColor,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  description: string;
  valueColor?: string;
}) {
  return (
    <Surface tone="solid" padding={20}>
      <div
        className="flex h-10 w-10 items-center justify-center rounded-full"
        style={{
          background: 'var(--color-surface-muted)',
          color: 'var(--color-accent-text)',
          border: '1px solid var(--color-surface-border)',
        }}
      >
        {icon}
      </div>

      <p className="text-eyebrow mt-4" style={{ color: 'var(--color-text-muted)' }}>
        {label}
      </p>
      <p className="text-panel-title mt-2" style={{ color: valueColor }}>
        {value}
      </p>
      <p className="text-meta mt-2">{description}</p>
    </Surface>
  );
}

function SessionCard({ item, index }: { item: ReviseHistoryItem; index: number }) {
  const title = item.title?.trim() || 'Revision session';
  const badgeVariant = getRevisionBadgeVariant(item.scorePct, item.status);
  const score = formatRevisionScore(item.scorePct, item.status);
  const headlineLabel = item.status === 'abandoned' ? 'Status' : 'Accuracy';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, delay: Math.min(index * 0.04, 0.18), ease: EASE }}
    >
      <Link to={`/revision/history/${item.id}`} className="block no-underline">
        <Surface interactive tone="solid" padding={22}>
          <div className="flex items-start justify-between gap-5">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2.5">
                <Badge variant={badgeVariant}>{score}</Badge>
                <Badge variant="surface">{getRevisionStatusLabel(item.status)}</Badge>
                <Badge variant="muted">{formatTimeOfDay(item.startedAt)}</Badge>
              </div>

              <h3 className="text-card-title mt-4 break-words">{title}</h3>

              <p className="text-meta mt-2">
                {formatRevisionQuestionSummary(
                  item.questionsCorrect,
                  item.questionsTotal,
                  item.status
                )}{' '}
                · {formatRevisionDurationMinutes(item.durationMinutes)}
              </p>
            </div>

            <div className="shrink-0 text-right">
              <p className="text-eyebrow" style={{ color: 'var(--color-text-muted)' }}>
                {headlineLabel}
              </p>
              <p
                className="text-panel-title mt-2"
                style={{ color: getRevisionScoreColor(item.scorePct, item.status) }}
              >
                {score}
              </p>
            </div>
          </div>

          {item.tags && item.tags.length > 0 ? (
            <div className="mt-5 flex flex-wrap gap-2">
              {item.tags.slice(0, 4).map((tag) => (
                <Tag key={tag.name}>{tag.displayName}</Tag>
              ))}
            </div>
          ) : null}

          <div
            className="mt-5 flex items-center justify-between gap-4 pt-4"
            style={{ borderTop: '1px solid var(--color-surface-border)' }}
          >
            <p className="text-meta">Open the full step-by-step review for this session.</p>
            <span
              className="text-meta inline-flex items-center gap-1.5"
              style={{ color: 'var(--color-accent-text)' }}
            >
              Review session
              <ArrowRight size={15} strokeWidth={2.1} />
            </span>
          </div>
        </Surface>
      </Link>
    </motion.div>
  );
}

function StateCard({
  eyebrow,
  title,
  body,
  illustration,
  action,
}: {
  eyebrow: string;
  title: string;
  body: string;
  illustration?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Surface tone="solid" padding={28}>
      <div className="flex flex-col gap-6 learner-double:flex-row learner-double:items-center learner-double:justify-between">
        <div className="min-w-0">
          <p className="text-eyebrow" style={{ color: 'var(--color-accent-text)' }}>
            {eyebrow}
          </p>
          <h2 className="text-section-title mt-3" style={{ maxWidth: '18ch' }}>
            {title}
          </h2>
          <p className="text-body mt-4" style={{ maxWidth: 'var(--measure-panel-copy)' }}>
            {body}
          </p>

          {action ? <div className="mt-6">{action}</div> : null}
        </div>

        {illustration ? <div className="shrink-0">{illustration}</div> : null}
      </div>
    </Surface>
  );
}

function HistorySkeleton() {
  return (
    <div className="space-y-8">
      {[0, 1].map((group) => (
        <div key={group}>
          <div className="flex items-center gap-3">
            <div
              className="h-3 w-24 rounded-full"
              style={{ background: 'var(--color-surface-muted)' }}
            />
            <div className="h-px flex-1" style={{ background: 'var(--color-surface-border)' }} />
          </div>

          <div className="mt-4 grid gap-4 learner-double:grid-cols-2">
            {[0, 1].map((card) => (
              <Surface key={card} tone="solid" padding={22}>
                <div
                  className="h-6 w-40 rounded-full"
                  style={{ background: 'var(--color-surface-muted)' }}
                />
                <div
                  className="mt-4 h-8 w-3/4 rounded-full"
                  style={{ background: 'var(--color-surface-muted)' }}
                />
                <div
                  className="mt-3 h-5 w-1/2 rounded-full"
                  style={{ background: 'var(--color-surface-muted)' }}
                />
              </Surface>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
